import { useState } from "preact/hooks";
import type { Override } from "../character";
import type { ModifierView } from "../engine";
import { RANK_LABELS, signed } from "../engine";
import { longPress } from "./long-press";
import { Modal } from "./modal";
import type { RollOptions } from "./rolls";
import { rollOutlook } from "./rolls";
import type { StatDetail } from "./stat-detail";
import { withOverride } from "./stat-detail";

interface Props {
  detail: StatDetail;
  /** The Overrides the detail's key lives in (the Character's, or the Familiar's). */
  overrides: Override[];
  busy: boolean;
  onOverrides: (next: Override[]) => Promise<void>;
  onClose: () => void;
  /** A DC to measure the statistic against. */
  dc?: number | null;
  /** Roll the statistic (#19): set when the sheet rolls it; a long press asks for the physical die. */
  onRoll?: ((options: RollOptions) => void) | undefined;
}

/**
 * The statistic panel (issue #14): tap any number on the sheet and this shows every modifier with
 * its type, value, source and enabled state, the Unsupported Rule Elements naming the statistic,
 * and the Override control. Given a DC it also shows the d20 each degree of success needs, which
 * is what a Roll button does until #19 rolls.
 */
export function StatPanel({ detail, overrides, busy, onOverrides, onClose, dc, onRoll }: Props) {
  const rollHandlers = onRoll ? longPress<HTMLButtonElement>((e) => (e.currentTarget instanceof HTMLButtonElement ? e.currentTarget : null), () => onRoll({}), () => onRoll({ physical: true })) : null;
  return (
    <Modal title={detail.label} onClose={onClose} class="stat-panel" header={rollHandlers ? <button type="button" class="roll" title="Tap to roll; hold to enter a physical die" disabled={busy} {...rollHandlers}><span class="roll-label">Roll</span></button> : undefined}>
      <p class="stat-headline">
        <span class={`stat-big${detail.override ? " overridden" : ""}`}>{detail.shown}</span>
        {detail.override && (
          <span class="muted">
            {" "}Override{detail.engine !== null ? ` · engine ${detail.engine}` : ""}{detail.override.note ? ` · ${detail.override.note}` : ""}
          </span>
        )}
      </p>
      {dc !== null && dc !== undefined && detail.form === "modifier" && <Outlook modifier={detail.value} dc={dc} />}
      <Breakdown detail={detail} />
      {detail.form !== "none" && <OverrideControl detail={detail} overrides={overrides} busy={busy} onOverrides={onOverrides} />}
    </Modal>
  );
}

function Outlook({ modifier, dc }: { modifier: number; dc: number }) {
  const o = rollOutlook(modifier, dc);
  const cell = (n: number | null, top = false) => (n === null ? "—" : top ? `${n}+` : String(n));
  return (
    <div class="outlook" aria-label={`Against DC ${dc}`}>
      <p class="muted">Against DC {dc} with {signed(modifier)}, the d20 needs:</p>
      <div class="outlook-grid">
        <span><span class="stat-label">Crit success</span><span class="stat-value">{cell(o.criticalSuccess, true)}</span></span>
        <span><span class="stat-label">Success</span><span class="stat-value">{cell(o.success, true)}</span></span>
        <span><span class="stat-label">Failure</span><span class="stat-value">{o.failure === null ? "—" : `${o.failure}${o.success !== null && o.success - 1 > o.failure ? `–${o.success - 1}` : ""}`}</span></span>
        <span><span class="stat-label">Crit failure</span><span class="stat-value">{o.criticalFailure === null ? "—" : `≤ ${o.criticalFailure}`}</span></span>
      </div>
      <p class="muted">A natural 20 or 1 shifts the degree one step.</p>
    </div>
  );
}

/** The modifiers and the roll extras behind a statistic, one row each; disabled ones muted. */
export function Breakdown({ detail }: { detail: StatDetail }) {
  const shown = detail.modifiers.filter((m) => m.enabled || !m.hideIfDisabled);
  const granter = (m: ModifierView): string | null => (m.source ? (detail.grantedBy.get(m.source) ?? null) : null);
  const extras = detail.extras;
  const notes = extras?.notes ?? [];
  const adjustments = extras?.degreeAdjustments ?? [];
  const substitutions = extras?.substitutions ?? [];
  const ephemeral = extras?.ephemeralEffects ?? [];
  const nothing = shown.length === 0 && detail.dice.length === 0 && notes.length === 0 && adjustments.length === 0 && substitutions.length === 0 && ephemeral.length === 0 && !extras?.rollTwice;
  return (
    <section class="breakdown" aria-label="Breakdown">
      <h3>Breakdown</h3>
      <ul class="plain breakdown-rows">
        {detail.base && <li class="base"><span class="bd-label">{detail.base}</span></li>}
        {shown.map((m, i) => (
          <li key={i} class={m.enabled ? "" : "off"}>
            <span class="bd-label">
              {m.label}
              <span class="tag">{m.type}</span>
              {m.damageType ? <span class="tag">{m.damageCategory ? `${m.damageCategory} ` : ""}{m.damageType}</span> : null}
              {m.critical === true ? <span class="tag">critical only</span> : null}
              {!m.enabled && <span class="tag">off</span>}
            </span>
            <span class="bd-source muted">
              {m.sourceName ?? ""}{granter(m) ? ` · granted by ${granter(m)}` : ""}{m.adjustedBy.length > 0 ? ` · adjusted by ${m.adjustedBy.join(", ")}` : ""}
            </span>
            <span class="bd-value">{signed(m.value)}</span>
          </li>
        ))}
        {detail.dice.map((d, i) => <li key={`d-${i}`}><span class="bd-label"><span class="tag">dice</span> {d}</span></li>)}
        {notes.map((n, i) => <li key={`n-${i}`} class={n.enabled ? "" : "off"}><span class="bd-label"><span class="tag">note</span> {n.title ? <strong>{n.title}: </strong> : null}<span dangerouslySetInnerHTML={{ __html: n.text }} />{n.outcome.length > 0 ? <span class="muted"> ({n.outcome.join(", ")})</span> : null}</span><span class="bd-source muted">{n.sourceName ?? ""}</span></li>)}
        {adjustments.map((a, i) => <li key={`a-${i}`} class={a.enabled ? "" : "off"}><span class="bd-label"><span class="tag">degree</span> {Object.entries(a.adjustments).map(([outcome, adj]) => `${outcome}: ${String(adj?.amount)}`).join(", ")}</span><span class="bd-source muted">{a.sourceName ?? ""}</span></li>)}
        {substitutions.map((s, i) => <li key={`s-${i}`} class={s.enabled ? "" : "off"}><span class="bd-label"><span class="tag">{s.effectType}</span> {s.label}: d20 = {s.value}{s.required ? " (required)" : ""}</span><span class="bd-source muted">{s.sourceName ?? ""}</span></li>)}
        {extras?.rollTwice ? <li><span class="bd-label"><span class="tag">fortune</span> roll twice, {extras.rollTwice === "keep-higher" ? "keep higher" : "keep lower"}</span></li> : null}
        {ephemeral.map((e, i) => <li key={`e-${i}`} class={e.enabled ? "" : "off"}><span class="bd-label"><span class="tag">effect</span> {e.name ?? e.uuid} on the {e.affects}</span><span class="bd-source muted">{e.sourceName ?? ""}</span></li>)}
        {nothing && detail.summary && <li><span class="bd-label muted">{detail.summary}</span></li>}
        {nothing && !detail.summary && <li><span class="bd-label muted">No modifiers.</span></li>}
      </ul>
      <h3>Unsupported Rule Elements</h3>
      {detail.unsupported.length === 0 ? (
        <p class="muted">None name this statistic.</p>
      ) : (
        <ul class="plain breakdown-rows unsupported">
          {detail.unsupported.map((u) => (
            <li key={`${u.uuid}:${u.index}`}>
              <span class="bd-label"><span class="tag warn">{u.key}</span> {u.name}</span>
              <span class="bd-source muted">{u.selector ?? ""}{u.support === "unknown" ? " · unknown to Foundry" : u.support === "stopped" ? " · stopped by the engine" : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Set, change or clear the Override on a statistic: a number (or a rank), an optional reason. */
export function OverrideControl({ detail, overrides, busy, onOverrides }: { detail: StatDetail; overrides: Override[]; busy: boolean; onOverrides: (next: Override[]) => Promise<void> }) {
  const [value, setValue] = useState(detail.override ? String(detail.override.value) : "");
  const [note, setNote] = useState(detail.override?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const apply = async () => {
    const raw = value.trim();
    if (raw === "") return clear();
    const n = Number(raw);
    if (detail.form === "rank" ? !Number.isInteger(n) || n < 0 || n > 4 : !Number.isFinite(n)) {
      setError(detail.form === "rank" ? "A rank is a whole number from 0 to 4." : "An Override is a number.");
      return;
    }
    setError(null);
    await onOverrides(withOverride(overrides, detail.key, n, note));
  };
  const clear = async () => {
    setError(null);
    setValue("");
    setNote("");
    await onOverrides(withOverride(overrides, detail.key, null, ""));
  };
  const unit = detail.form === "speed" ? "ft" : detail.form === "dc" ? "" : "";
  return (
    <section class="override" aria-label="Override">
      <h3>Override</h3>
      <p class="muted">A value set here replaces the engine's on the sheet and is marked as an Override.</p>
      <div class="override-form">
        {detail.form === "rank" ? (
          <select aria-label="Override rank" value={value} disabled={busy} onChange={(e) => setValue((e.currentTarget as HTMLSelectElement).value)}>
            <option value="">No Override</option>
            {RANK_LABELS.map((label, i) => <option key={i} value={String(i)}>{label}</option>)}
          </select>
        ) : (
          <label class="override-value">
            <input type="text" inputMode={detail.form === "modifier" ? "text" : "numeric"} aria-label="Override value" placeholder={detail.form === "modifier" ? "e.g. +7" : "e.g. 20"} value={value} disabled={busy} onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)} />
            {unit && <span class="muted">{unit}</span>}
          </label>
        )}
        <input type="text" aria-label="Reason" placeholder="Reason (optional)" value={note} disabled={busy} onInput={(e) => setNote((e.currentTarget as HTMLInputElement).value)} />
        <span class="actions">
          <button type="button" class="primary" disabled={busy} onClick={() => void apply()}>Apply</button>
          {detail.override && <button type="button" disabled={busy} onClick={() => void clear()}>Clear</button>}
        </span>
      </div>
      {error && <p class="notice" role="alert">{error}</p>}
    </section>
  );
}
